// MA-891 — the badge catalogue per child (KID-JOURNEY-2026-10-08 D-KJ-8, §5).
//
// Built over the REAL Journey view (buildJourneyView / buildScreenJourneyView: the 54
// chapter stones, their tree status and tapEligibility), the REAL pick predicate
// (isStonePickEligible, the MA-497 classes), the REAL zero-pool check (isZeroPoolSkill)
// and the tree's own below-band set (ageCoveredForTreeSkillIds, the set the fold
// passes as `ageCovered`). Pure: no React, no storage.

import type { Status } from "../skillTreeStatus";
import type { JourneyStone, JourneyView } from "../journeyView";
import { isZeroPoolSkill, KID_LABEL_MAP } from "../journeyView";
import { isStonePickEligible } from "../journeyStoneRender";
import type { StoneTapProgressLike } from "../journeyTap";
import { ageCoveredForTreeSkillIds } from "../parentPriors";
import chaptersManifest from "@/curriculum/data/manifests/chapters.json" with { type: "json" };
import { PRACTICE_DAY_THRESHOLDS, chapterBadgeId, daysBadgeId, skillBadgeId } from "./ids";

/** Tree statuses that mean "the child has attempted this below-band skill"
 *  (statusFor: a below-band skill reads mastered / secure / not_yet once tried). */
const ATTEMPTED_BELOW_BAND: ReadonlySet<Status> = new Set<Status>(["not_yet", "secure", "mastered"]);

interface ManifestChapter {
  id: string;
  titleKid?: string;
  milestoneSkills: string[];
  supportSkills: string[];
  celebrationPayload?: { badge?: string };
}

const MANIFEST_CHAPTERS: ManifestChapter[] = (chaptersManifest as unknown as { chapters: ManifestChapter[] }).chapters;

/** Every chapter skill id (milestone ∪ support, chapter order), from chapters.json:
 *  the set a latched or derived skill badge is looked up over, rendered or not. */
export const CHAPTER_SKILL_IDS: readonly string[] = Object.freeze([
  ...new Set(MANIFEST_CHAPTERS.flatMap((c) => [...c.milestoneSkills, ...c.supportSkills])),
]);

/** Every chapter's kid title (the name a trophy carries). */
export const CHAPTER_TITLES_KID: readonly string[] = Object.freeze(
  MANIFEST_CHAPTERS.map((c) => c.titleKid).filter((t): t is string => typeof t === "string"),
);

/** chapter id → its trophy id (`celebrationPayload.badge`, chapters.json). */
export const CHAPTER_TROPHY: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(
    MANIFEST_CHAPTERS
      .filter((c) => typeof c.celebrationPayload?.badge === "string")
      .map((c) => [c.id, c.celebrationPayload!.badge!]),
  ),
);

export interface SkillBadgeEntry {
  family: "skill";
  id: string;
  skillId: string;
  chapterId: string;
  kidLabel: string;
  status: Status;
  tapEligibility: JourneyStone["tapEligibility"];
  pickEligible: boolean;
  belowBand: boolean;
  /** A below-band skill the child has not tried, shown because it is pick-eligible today. */
  tryIt: boolean;
}

export interface TrophyEntry {
  family: "chapter";
  id: string;
  chapterId: string;
  titleKid: string;
  /** The catalogue skill ids of this chapter (the trophy needs every one gold). */
  skillIds: string[];
}

export interface DaysEntry {
  family: "days";
  id: string;
  threshold: number;
}

export interface BadgeCatalogue {
  skills: SkillBadgeEntry[];
  trophies: TrophyEntry[];
  days: DaysEntry[];
}

export interface CatalogueInput {
  /** The Journey view the child sees (buildScreenJourneyView over the tree fold). */
  view: JourneyView;
  /** The TREE fold's statuses (computeStrandStatuses), the same object the view was
   *  built from. Needed because the view maps `not_yet` to `age_covered`
   *  (journeyStatusOf, journeyView.ts:554) and D-KJ-8 keys on the tree status. */
  statusById: Readonly<Record<string, Status | undefined>>;
  /** The child's selection age (resolveSelectionAge), for the tree's below-band set. */
  age: number | null | undefined;
  /** isStonePickEligible's inputs, exactly as JourneyPath passes them. */
  progress: StoneTapProgressLike | null | undefined;
  registryAge?: number | null;
  valveLowered?: boolean;
  /** Skill ids whose skill badge (any stage) is already earned (latched or derived):
   *  a parent-removed skill stays in the book only then. */
  earnedSkillIds?: ReadonlySet<string>;
}

/** The catalogue for one child (D-KJ-8). */
export function buildBadgeCatalogue(input: CatalogueInput): BadgeCatalogue {
  const belowBand = new Set(ageCoveredForTreeSkillIds(input.age));
  const earned = input.earnedSkillIds ?? new Set<string>();
  const skills: SkillBadgeEntry[] = [];
  const trophies: TrophyEntry[] = [];
  for (const chapter of input.view.chapters) {
    const chapterSkillIds: string[] = [];
    // The view never renders a suppressed stone (journeyView.ts:433, D2), so an
    // already-earned parent-removed skill (D-KJ-16: hidden unless earned) is
    // re-attached from the chapter manifest, untappable.
    const rendered = [...chapter.readingStones, ...chapter.mathStones];
    const renderedIds = new Set(rendered.map((st) => st.id));
    const manifest = MANIFEST_CHAPTERS.find((c) => c.id === chapter.id);
    const removedEarned: JourneyStone[] = (manifest ? [...manifest.milestoneSkills, ...manifest.supportSkills] : [])
      .filter((id) => !renderedIds.has(id) && input.statusById[id] === "suppressed" && earned.has(id))
      .map((id) => ({ id, kind: "pebble", kidLabel: KID_LABEL_MAP[id] ?? id, status: "suppressed", ariaLabel: "", tapEligibility: "none", tappable: false }));
    for (const stone of [...rendered, ...removedEarned]) {
      const status: Status = input.statusById[stone.id] ?? stone.status;
      if (isZeroPoolSkill(stone.id)) continue;
      // Defensive: the screen's view never renders a suppressed stone (journeyView.ts:433),
      // so for rendered stones this skip is unreachable; it guards a caller that passes a
      // view built without that filter. Earned suppressed skills arrive via removedEarned.
      if (status === "suppressed" && !earned.has(stone.id)) continue;
      const pickEligible = isStonePickEligible(stone, input.progress, input.registryAge ?? null, input.valveLowered ?? false);
      const below = belowBand.has(stone.id);
      const attempted = ATTEMPTED_BELOW_BAND.has(status);
      const shown = !below || attempted || pickEligible || earned.has(stone.id);
      if (!shown) continue;
      skills.push({
        family: "skill",
        id: skillBadgeId(stone.id),
        skillId: stone.id,
        chapterId: chapter.id,
        kidLabel: stone.kidLabel,
        status,
        tapEligibility: stone.tapEligibility,
        pickEligible,
        belowBand: below,
        tryIt: below && !attempted && !earned.has(stone.id),
      });
      chapterSkillIds.push(stone.id);
    }
    const trophy = CHAPTER_TROPHY[chapter.id];
    if (trophy && chapterSkillIds.length > 0) {
      trophies.push({ family: "chapter", id: chapterBadgeId(trophy), chapterId: chapter.id, titleKid: chapter.titleKid, skillIds: chapterSkillIds });
    }
  }
  const days: DaysEntry[] = PRACTICE_DAY_THRESHOLDS.map((n) => ({ family: "days", id: daysBadgeId(n), threshold: n }));
  return { skills, trophies, days };
}
